import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, Clock, Inbox } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { VideraMark } from "@/components/VideraMark";
import { toast } from "sonner";

const STATUSES = ["Open", "In Progress", "Resolved"];

const STATUS_STYLE = {
  Open: "bg-amber-50 text-amber-700 border-amber-200",
  "In Progress": "bg-sky-50 text-sky-700 border-sky-200",
  Resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const when = (v) => (v ? new Date(v).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "");

export default function PlatformTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = async (keepId) => {
    try {
      const { data } = await api.get("/platform/tickets", { params: { status: filter || undefined } });
      setTickets(data);
      const keep = keepId || selected?.id;
      setSelected(keep ? data.find((t) => t.id === keep) || data[0] || null : data[0] || null);
    } catch (e) {
      if (e.response?.status === 404) return navigate("/dashboard");
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const send = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      const { data } = await api.post(`/platform/tickets/${selected.id}/reply`, { message: reply.trim() });
      setReply("");
      toast.success(data.emailed ? "Reply sent and emailed to the customer" : "Reply saved — email could not be sent");
      await load(selected.id);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/platform/tickets/${selected.id}/status`, { status });
      toast.success(`Marked ${status}`);
      await load(selected.id);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur">
        <div className="mx-auto flex max-w-[1500px] items-center gap-4 px-6 py-4">
          <VideraMark size={20} className="text-amber-400" />
          <div className="mr-auto">
            <p className="text-sm font-semibold tracking-tight">Support queue</p>
            <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Videra Technology — owner only</p>
          </div>
          <Button data-testid="tickets-back-btn" variant="outline" size="sm" onClick={() => navigate("/platform")}
            className="gap-1.5 border-white/15 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white">
            <ArrowLeft size={14} /> Analytics
          </Button>
        </div>
      </header>

      <main data-testid="platform-tickets" className="mx-auto grid max-w-[1500px] gap-4 px-6 py-8 lg:grid-cols-[340px_1fr]">
        <div className="panel overflow-hidden">
          <div className="flex items-center gap-1 border-b border-slate-200 px-3 py-2">
            {["", ...STATUSES].map((s) => (
              <button key={s || "all"} data-testid={`ticket-filter-${s || "all"}`} onClick={() => setFilter(s)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  filter === s ? "bg-[#1F4E79] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}>
                {s || "All"}
              </button>
            ))}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {tickets.length === 0 && (
              <p className="px-4 py-10 text-center text-sm text-slate-500">No tickets yet.</p>
            )}
            {tickets.map((t) => (
              <button key={t.id} data-testid={`ticket-item-${t.id}`} onClick={() => setSelected(t)}
                className={`w-full border-b border-slate-100 px-4 py-3 text-left transition-colors ${
                  selected?.id === t.id ? "bg-slate-100" : "hover:bg-slate-50"
                }`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold text-slate-500">{t.ref}</span>
                  <span className={`rounded-full border px-1.5 py-0.5 text-[10px] ${STATUS_STYLE[t.status]}`}>{t.status}</span>
                </div>
                <p className="mt-1 truncate text-sm font-medium text-slate-900">{t.subject}</p>
                <p className="truncate text-xs text-slate-500">{t.org_name} · {t.created_by_email}</p>
              </button>
            ))}
          </div>
        </div>

        {selected ? (
          <div data-testid="ticket-detail" className="panel px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{selected.ref}</p>
                <h2 className="text-xl font-bold text-slate-900">{selected.subject}</h2>
                <p className="mt-1 text-xs text-slate-500">
                  {selected.org_name} · {selected.plan} plan · {selected.category} · {selected.priority} priority ·
                  raised by {selected.created_by_name} ({selected.created_by_email}) {when(selected.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                {STATUSES.map((s) => (
                  <Button key={s} data-testid={`ticket-status-${s.replace(" ", "-").toLowerCase()}`}
                    size="sm" variant={selected.status === s ? "default" : "outline"}
                    onClick={() => setStatus(s)} className="gap-1 text-xs">
                    {s === "Resolved" ? <CheckCircle2 size={13} /> : s === "Open" ? <Inbox size={13} /> : <Clock size={13} />}
                    {s}
                  </Button>
                ))}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {(selected.messages || []).map((m, i) => (
                <div key={i} data-testid={`ticket-message-${i}`}
                  className={`rounded-lg border px-4 py-3 ${
                    m.side === "support" ? "border-[#1F4E79]/20 bg-[#1F4E79]/5" : "border-slate-200 bg-white"
                  }`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {m.side === "support" ? "Videra Support" : m.author_name} · {when(m.at)}
                  </p>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{m.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 border-t border-slate-200 pt-4">
              <Textarea data-testid="ticket-reply-input" rows={4} value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Reply to the customer — they will receive this by email." />
              <div className="mt-2 flex justify-end">
                <Button data-testid="ticket-reply-send" onClick={send} disabled={sending || !reply.trim()} className="gap-1.5">
                  <Send size={14} /> {sending ? "Sending…" : "Send reply"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="panel flex items-center justify-center px-6 py-20 text-sm text-slate-500">
            Select a ticket to read the conversation.
          </div>
        )}
      </main>
    </div>
  );
}
